/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { create } from 'zustand';

/**
 * A selectable impersonation identity for local dev. Carries the given/family
 * names so the mock auth layer can emit the same SSO claims a real Google/K4D
 * login provides (`given_name`, `family_name`, `name`, `full_name`).
 */
export interface MockUserProfile {
  id: string;
  label: string;
  /**
   * Papel que o seed canônico dá a este membro
   * (`release/tests/e2e_api/seed/catalogue/seed_members.py`). É rótulo, não fonte da
   * verdade: o papel efetivo é por agência e vem de `agency.members`.
   */
  role: 'owner' | 'admin' | 'operational' | 'viewer' | 'agent';
  email: string;
  agencyId: string | null;
  givenName: string;
  familyName: string;
}

const STORAGE_KEY_EMAIL = 'DEVCOCKPIT_impersonate_email';
const STORAGE_KEY_PROFILE = 'DEVCOCKPIT_active_profile';

const DEFAULT_PROFILES: MockUserProfile[] = [
  { id: 'jorge-owner', label: 'Jorge (Owner)', role: 'owner', email: 'jorge@k4d.com.br', agencyId: 'demo-mil-imoveis', givenName: 'Jorge', familyName: '' },
  { id: 'lucas-admin', label: 'Lucas (Admin/Multi-agency)', role: 'admin', email: 'lucas@k4d.com.br', agencyId: 'demo-mil-imoveis', givenName: 'Lucas', familyName: '' },
  { id: 'reis-admin', label: 'Reis (Admin)', role: 'admin', email: 'reis@k4d.com.br', agencyId: 'demo-mil-imoveis', givenName: 'Reis', familyName: '' },
  { id: 'barbosa-broker', label: 'Barbosa (Broker)', role: 'agent', email: 'barbosa@k4d.com.br', agencyId: 'demo-mil-imoveis', givenName: 'Barbosa', familyName: '' },
  { id: 'anacarla-broker', label: 'Ana Carla (Broker)', role: 'agent', email: 'anacarla@k4d.com.br', agencyId: 'demo-mil-imoveis', givenName: 'Ana Carla', familyName: '' },
  { id: 'belletti-broker', label: 'Belletti (Operacional)', role: 'operational', email: 'belletti@k4d.com.br', agencyId: 'demo-mil-imoveis', givenName: 'Belletti', familyName: '' },
  { id: 'e2e-broker', label: 'E2E Test (Admin)', role: 'admin', email: 'e2e-test@k4d.com.br', agencyId: 'demo-mil-imoveis', givenName: 'E2E', familyName: 'Test' },
];

interface AuthSwitcherState {
  profiles: MockUserProfile[];
  activeProfileId: string | null;
  customEmail: string | null;
  setActiveProfile: (id: string | null) => void;
  setCustomEmail: (email: string | null) => void;
  getActiveProfile: () => MockUserProfile | null;
  getActiveEmail: () => string | null;
}

/**
 * Reads the persisted active profile id from localStorage.
 *
 * @returns The stored profile id, or null when unset or storage is unavailable.
 */
function readStoredProfileId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY_PROFILE);
  } catch {
    return null;
  }
}

/**
 * Reads the persisted custom impersonation email from localStorage.
 *
 * @returns The stored email, or null when unset or storage is unavailable.
 */
function readStoredEmail(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY_EMAIL);
  } catch {
    return null;
  }
}

export const useAuthSwitcherStore = create<AuthSwitcherState>((set, get) => ({
  profiles: DEFAULT_PROFILES,
  activeProfileId: readStoredProfileId(),
  customEmail: readStoredEmail(),

  setActiveProfile: (id) => {
    set({ activeProfileId: id });
    try {
      if (id === null) {
        localStorage.removeItem(STORAGE_KEY_PROFILE);
      } else {
        localStorage.setItem(STORAGE_KEY_PROFILE, id);
      }
    } catch { /* storage unavailable */ }
  },

  setCustomEmail: (email) => {
    set({ customEmail: email });
    try {
      if (email === null) {
        localStorage.removeItem(STORAGE_KEY_EMAIL);
      } else {
        localStorage.setItem(STORAGE_KEY_EMAIL, email);
      }
    } catch { /* storage unavailable */ }
  },

  getActiveProfile: () => {
    const { profiles, activeProfileId } = get();
    if (!activeProfileId) return null;
    return profiles.find((p) => p.id === activeProfileId) ?? null;
  },

  getActiveEmail: () => {
    const { customEmail, profiles, activeProfileId } = get();
    if (customEmail) return customEmail;
    if (!activeProfileId) return null;
    const profile = profiles.find((p) => p.id === activeProfileId);
    return profile?.email ?? null;
  },
}));

/**
 * Capitalizes the first letter of a token, leaving the rest unchanged.
 *
 * @param token - The token to capitalize.
 * @returns The token with its first character uppercased, or empty string.
 */
function capitalize(token: string): string {
  if (!token) return '';
  return token.charAt(0).toUpperCase() + token.slice(1);
}

/**
 * Derives given/family display names from an email local-part when no
 * registered profile matches. 'ana.carla@x.com' -> { givenName: 'Ana',
 * familyName: 'Carla' }; a single-token local part yields an empty family.
 *
 * @param email - The impersonation email to derive a name from.
 * @returns Given and family names suitable for SSO claim emulation.
 */
export function deriveNameFromEmail(email: string): { givenName: string; familyName: string } {
  const local = email.split('@')[0] ?? '';
  const tokens = local.split(/[._-]+/).filter(Boolean).map(capitalize);
  return { givenName: tokens[0] ?? '', familyName: tokens.slice(1).join(' ') };
}

/**
 * Re-hydrates store state from localStorage. Useful for testing
 * scenarios where localStorage is populated after store creation.
 */
export function hydrateFromStorage(): void {
  useAuthSwitcherStore.setState({
    activeProfileId: readStoredProfileId(),
    customEmail: readStoredEmail(),
  });
}
